import React, { useEffect, useRef, useState } from "react";
import styles from "./addProductWizard.module.scss";
import AddProductMethod from "./AddProductMethod";
import { useDispatch, useSelector } from "react-redux";
import {
  selectFormData,
  selectProductMethod,
  selectSelectedProduct,
  selectSelectedStage,
  setRecipeOptions,
  setSelectedProduct,
  setSelectedStage,
} from "./AddProductWizardSlice";
import CopyFromExisting from "./CopyFromExisting";
import AddProductForm from "./AddProductForm";
import sharedDataApi from "../../../api/sharedDataApi";
import { productApi } from "../../../api/productApi";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/button/Button";

const AddProductWizard = ({ onAbort }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formErrorsExist, setFormErrorsExist] = useState(false); // Track if there are form errors
  const [modelNumberUnique, setModelNumberUnique] = useState(true); // New state for uniqueness
  const searchTimeoutId = useRef(null);

  const formData = useSelector(selectFormData);

  const selectedValue = useSelector(selectProductMethod);
  const selectedStage = useSelector(selectSelectedStage);
  const selectedProduct = useSelector(selectSelectedProduct);

  const dispatch = useDispatch();

  const [steps, setSteps] = useState([<AddProductMethod />]);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedValue === "copyFromExisting") {
      // dispatch(setSelectedStage("Staging"));
      setSteps([
        <AddProductMethod />,
        <CopyFromExisting />,
        <AddProductForm setFormErrorsExist={setFormErrorsExist} />,
      ]);
    } else {
      setSteps([
        <AddProductMethod />,
        <AddProductForm setFormErrorsExist={setFormErrorsExist} />,
      ]);
    }
  }, [selectedValue]);
  

  useEffect(() => {
    dispatch(setSelectedProduct(null));
    dispatch(setSelectedStage(null));
    // const getRecipeOptions = async () => {
    //   const res = await sharedDataApi.listSdpRecipes();
    //   const recipes = res.items.map((recipe) => ({
    //     label: recipe.Name,
    //     value: recipe,
    //   }));
    //   dispatch(setRecipeOptions(recipes));
    // };
    const getRecipeOptions = async () => {
      let allRecipes = [];
      let nextToken = null;
    
      do {
        const res = await sharedDataApi.listSdpRecipes({
          nextToken: nextToken,
        });
    
        const recipes = res.items.map((recipe) => ({
          label: recipe.Name,
          value: recipe,
        }));
    
        allRecipes = [...allRecipes, ...recipes];
        nextToken = res.nextToken;
      } while (nextToken);
    
      dispatch(setRecipeOptions(allRecipes));
    };
    getRecipeOptions();
  }, []);

  //debounces the validation of the "ModelNumber" field, checking its uniqueness after 500ms of user input and avoiding unnecessary API calls.
  useEffect(() => {
    const validateModelNumber = async () => {
      if (formData.ModelNumber) {
        try {
          // Use Elasticsearch search for case-insensitive matching
          const searchRes = await productApi.searchProducts({
            searchField: "ModelNumber",
            searchValue: formData.ModelNumber,
          });
          
          // Check if any result matches case-insensitively
          const hasMatch = searchRes.items.some(
            item => item.ModelNumber?.toLowerCase() === formData.ModelNumber.toLowerCase()
          );
          
          if (hasMatch) {
            setModelNumberUnique(false);
            return;
          }
          // Use Elasticsearch search for ActionItems
          const actionList = await sharedDataApi.searchActionItems({
            filter: {
              or: [
                { ModelNumber: { eq: formData.ModelNumber } },
                { ModelNumber: { matchPhrase: formData.ModelNumber } },
              ],
            },
          });
          const hasActionMatch = actionList.items.some(
            item => item.ModelNumber?.toLowerCase() === formData.ModelNumber.toLowerCase()
          );
          if (hasActionMatch) {
            setModelNumberUnique(false);
            return;
          }

          setModelNumberUnique(true);
        } catch (error) {
          console.error("Error validating model number:", error);
          setModelNumberUnique(false); 
        }
      } else {
        setModelNumberUnique(true);
      }
    };
    
    // Clear previous timeout if any
    if (searchTimeoutId.current) {
      clearTimeout(searchTimeoutId.current);
    }

    // Set a new timeout to validate after 2 seconds of the user typing
    searchTimeoutId.current = setTimeout(() => {
      validateModelNumber();
    }, 500);

    // Cleanup the timeout on component unmount or formData.ModelNumber change
    return () => {
      if (searchTimeoutId.current) {
        clearTimeout(searchTimeoutId.current);
      }
    };
  }, [formData.ModelNumber]);

  const isNextDisabled = () => {
    if (currentStep === 1 && !selectedProduct) {
      return true;
    }
    return false;
  };

  const isFinishDisabled = () => {
    console.log(selectedStage);

    const areNumericFieldsValid =
      formData.ControlId >= 0 &&
      formData.ControlId <= 1000 &&
      formData.ControlType >= 0 &&
      formData.ControlType <= 1000 &&
      formData.SoftwareVersion >= 0 &&
      formData.SoftwareVersion <= 1000 &&
      formData.ShareddataVersion >= 0 &&
      formData.ShareddataVersion <= 1000;

    if (
      !formErrorsExist &&
      modelNumberUnique &&
      // (selectedStage || selectedValue !== "copyFromExisting") &&
      formData.ModelNumber &&
      formData.Family &&
      formData.Category &&
      (formData.ControlId !== null && formData.ControlId !== undefined && formData.ControlId !== "") &&
      formData.SubCategory &&
      formData.Recipe &&
      (formData.ControlType !== null && formData.ControlType !== undefined && formData.ControlType !== "") &&
      (formData.SoftwareVersion !== null && formData.SoftwareVersion !== undefined && formData.SoftwareVersion !== "") &&
      (formData.ShareddataVersion !== null && formData.ShareddataVersion !== undefined && formData.ShareddataVersion !== "") &&
      typeof formData.BtEnabled === "boolean" &&
      areNumericFieldsValid // Ensure numeric fields are valid
    ) {
      return false;
    }
    return true;
  };

  const next = () => {
    if (currentStep < steps.length - 1 && !isNextDisabled()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const back = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (isFinishDisabled()) {
      return;
    }
    await createProduct();
  };

  const createProduct = async () => {
    const newProduct = { ...formData };
    newProduct.SdAvailable = false;
    newProduct.Recipe = newProduct.Recipe.Name || newProduct.Recipe;
    newProduct.RecipeDescription = newProduct.Recipe.Description || "";
    newProduct.ProductionVersion = 0;
    newProduct.StagingVersion = 0;
    newProduct.State = null;
    newProduct.CloneFromProductId = null;
    newProduct.CoolCloudDBAck = false;
    newProduct.AMSAck = false;

    if (selectedValue === "copyFromExisting" && selectedProduct?.id) {
      newProduct.CloneFromProductId = selectedProduct.id;
    }
  
    delete newProduct.id;
    delete newProduct.createdAt;
    delete newProduct.updatedAt;
    delete newProduct.__typename;
  
    // Ensure Category is always a valid JSON string as per requirements
    if (typeof newProduct.Category === "string") {
      try {
        JSON.parse(newProduct.Category);
      } catch (e) {
        // Not valid JSON, wrap as {"Note": "<original_input>"}
        newProduct.Category = JSON.stringify({ Note: newProduct.Category });
      }
    } else if (typeof newProduct.Category === "object" && newProduct.Category !== null) {
      // If it's an object, stringify it
      newProduct.Category = JSON.stringify(newProduct.Category);
    }
  
    const createdProduct = await productApi.createProduct({ newProduct });
  
    onAbort();
  
    if (selectedValue === "copyFromExisting") {
      // const version = selectedProduct[`${selectedStage}Version`];
      const version =
        selectedStage === "Draft"
          ? 1
          : selectedProduct[`${selectedStage}Version`];
      navigate(
        `/products/create-shared-data?id=${createdProduct.id}&model=${selectedProduct.ModelNumber}&stage=${selectedStage}&version=${version}&cloneId=${formData.id}`
      );
    } else {
      navigate(`/products/create-shared-data?id=${createdProduct.id}`);
    }
  };

  return (
    <div className={styles.wizardContainer}>
      <div className={styles.layout}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <div className={styles.title}>Add Product</div>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progress}
              style={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
            />
          </div>
          <div className={styles.stepContainer}>
            {React.cloneElement(steps[currentStep])}
          </div>

          <div className={styles.buttonContainer}>
            <Button style={styles.cancel} onClick={onAbort}>
              Cancel
            </Button>
            <div className={styles.buttonContainer}>
              <Button
                onClick={back}
                style={
                  currentStep === 0 ? styles.buttonDisable : styles.onSuccess
                }
                disabled={currentStep === 0}
              >
                Back
              </Button>
              {currentStep < steps.length - 1 ? (
                <Button
                  style={
                    isNextDisabled() ? styles.buttonDisable : styles.onSuccess
                  }
                  disabled={isNextDisabled()}
                  onClick={next}
                >
                  Next
                </Button>
              ) : (
                <Button
                  style={
                    isFinishDisabled() ? styles.buttonDisable : styles.onSuccess
                  }
                  disabled={isFinishDisabled()}
                  onClick={handleSubmit}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProductWizard;
